import { useMemo } from "react";
import { useQueries } from "react-query";
import { useRole } from "hooks/common";
import { useIsClassroom } from "hooks/common/useIsClassroom/useIsClassroom";
import { useParams } from "react-router-dom";
import { useHomeworkExercises } from "hooks/api/exercises";
import { useCourseLessonChapters } from "hooks/api/courses";
import { useExerciseConstructor, useLessonSettings } from "hooks/redux";
import { getChapterStudentProgress } from "api/services/student-progress";
import { EStudentProgressQueryKeys } from "api/endpoints/student-progress";
import { LessonDetailsContent } from "content";
import { Button } from "components/Interactive/Button";
import { Divider } from "components/Common/Divider";
import { Space } from "components/Common/Space";
import { StudentSwitcher } from "components/Common/StudentSwitcher";
import { ExercisesPreview } from "components/Lesson";
import { TExercise } from "types/api/exercise";
import "./HomeworkContentExercises.styles.scss";

type HomeworkContentExercisesProps = {
  /**
   * Unique identifier of the lesson homework entity.
   */
  homeworkId?: number;
  /**
   * Student ID for group context (when studentId is not in URL params)
   * @default undefined
   */
  overrideStudentId?: string;
  /**
   * Props for StudentSwitcher component (for groups)
   * @default undefined
   */
  studentSwitcherProps?: {
    studentIds: number[];
    selectedStudentId?: number;
    onStudentSelect: (studentId: number) => void;
    loading?: boolean;
  };
};

export const HomeworkContentExercises: React.FC<
  HomeworkContentExercisesProps
> = (props) => {
  const { homeworkId, overrideStudentId, studentSwitcherProps } = props;

  const { courseId, lessonId, studentId: urlStudentId } = useParams();
  const studentId = overrideStudentId || urlStudentId;
  const { isUserAdmin } = useRole();
  const { isClassroom } = useIsClassroom();

  const { data: exercisesData, isLoading: exercisesLoading } =
    useHomeworkExercises(
      !isClassroom ? courseId : null,
      !isClassroom ? lessonId : null,
      !isClassroom ? homeworkId : null
    );

  const { data: chapters = [], isLoading: chaptersLoading } =
    useCourseLessonChapters(
      isClassroom ? courseId : undefined,
      isClassroom ? lessonId : undefined
    );

  const chapterProgressResults = useQueries(
    isClassroom
      ? chapters.map((chapter) => ({
          queryKey: [
            EStudentProgressQueryKeys.getChapterStudentProgress,
            courseId,
            lessonId,
            chapter.id,
            studentId,
          ],
          queryFn: () =>
            getChapterStudentProgress(
              courseId,
              lessonId,
              String(chapter.id),
              studentId
            ),
          enabled: !!(courseId && lessonId && studentId),
          retry: false,
        }))
      : []
  );

  const progressByChapter = useMemo(() => {
    return chapters
      .map((chapter, index) => ({
        chapterId: chapter.id,
        chapterOrder: index + 1,
        exercises: (
          (chapterProgressResults[index]?.data as TExercise[]) ?? []
        ).filter((e) => e.isHomework),
      }))
      .filter(({ exercises }) => exercises.length > 0);
  }, [chapterProgressResults, chapters]);

  const progressLoading =
    chaptersLoading || chapterProgressResults.some((r) => r.isLoading);

  const isLoading = isClassroom ? progressLoading : exercisesLoading;

  const { openExerciseList } = useExerciseConstructor();
  const { viewMode } = useLessonSettings();

  const openExerciseConstructorClickHandler = () => {
    openExerciseList();
  };

  return (
    <div className="nb-lesson-details-homework-exercises-wrapper">
      <div className="nb-lesson-details-homework-exercises">
        {isClassroom &&
          studentSwitcherProps &&
          studentSwitcherProps.studentIds.length > 1 && (
            <Space direction="horizontal" justify="end" fullWidth>
              <StudentSwitcher
                studentIds={studentSwitcherProps.studentIds}
                selectedStudentId={studentSwitcherProps.selectedStudentId}
                onStudentSelect={studentSwitcherProps.onStudentSelect}
                loading={studentSwitcherProps.loading}
              />
            </Space>
          )}
        {isClassroom ? (
          progressByChapter.map(({ chapterId, chapterOrder, exercises }) => (
            <ExercisesPreview
              key={chapterId}
              exercises={exercises}
              chapterId={chapterId}
              chapterOrder={chapterOrder}
              loading={isLoading}
              isHomeworkExercises={false}
            />
          ))
        ) : (
          <ExercisesPreview
            exercises={exercisesData}
            chapterId={1}
            loading={isLoading}
            isHomeworkExercises={true}
          />
        )}
        {viewMode === "constructor" && isUserAdmin && (
          <Button
            variant="secondary"
            fullWidth
            onClick={openExerciseConstructorClickHandler}
          >
            {LessonDetailsContent.Content.Homework.Button.ADD}
          </Button>
        )}
      </div>
      <Divider
        margin="yAxis"
        marginSize="large"
        className="nb-lesson-details-homework-exercises-divider"
      />
    </div>
  );
};
